import { Empty, Input, Spin } from 'antd';
import moment from 'moment';
import React from 'react';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { SortDirectionEnum } from '../../../core/models/enum';
import { errorWithUserMessage } from '../../../helpers/NotificationHelper';
import { includesForSearch } from '../../../helpers/normalizeForSearch';
import {
    MatchesClient,
    MatchFilter,
    MatchVm,
    PaginationFilterOfMatchFilter,
} from '../../../utils/api';
import MobileShell from '../MobileShell';
import { Props } from './index';

interface State {
    matches: MatchVm[];
    loading: boolean;
    search: string;
}

class MobileMatchList extends React.PureComponent<Props, State> {
    private isMounted_ = false;

    public constructor(props: Props) {
        super(props);
        this.state = {
            matches: [],
            loading: true,
            search: '',
        };
    }

    public componentDidMount = (): void => {
        this.isMounted_ = true;
        this.getMatches();
    };

    public componentWillUnmount = (): void => {
        this.isMounted_ = false;
    };

    private getMatches = async (): Promise<void> => {
        try {
            const matchClient = new MatchesClient();
            const result = await matchClient.getPaginated(
                new PaginationFilterOfMatchFilter({
                    filter: new MatchFilter(),
                    pageIndex: 1,
                    pageSize: 50,
                    sortBy: 'StartDate',
                    sortOrder: SortDirectionEnum.DESC,
                })
            );
            if (!this.isMounted_) return;
            this.setState({ matches: result.pageItems || [], loading: false });
        } catch (error) {
            if (!this.isMounted_) return;
            errorWithUserMessage('Could not load matches.');
            this.setState({ loading: false });
        }
    };

    private openMatch = (matchId: number): void => {
        this.props.history.push(
            Routes.ROUTE_MOBILE_MATCH_LOG.replace(ReplaceStrings.MATCH_ID, matchId.toString())
        );
    };

    private filteredMatches = (): MatchVm[] => {
        const { matches, search } = this.state;
        const term = search.trim();
        if (!term) return matches;
        return matches.filter(
            (m) =>
                includesForSearch(m.homeTeamDisplayName || '', term) ||
                includesForSearch(m.awayTeamDisplayName || '', term) ||
                includesForSearch(m.competitionDisplayName || '', term)
        );
    };

    private renderScore = (match: MatchVm): string => {
        if (
            match.homeTeamGoalsTotal !== undefined &&
            match.homeTeamGoalsTotal !== null &&
            match.awayTeamGoalsTotal !== undefined &&
            match.awayTeamGoalsTotal !== null
        ) {
            return `${match.homeTeamGoalsTotal} : ${match.awayTeamGoalsTotal}`;
        }
        return 'vs';
    };

    public render(): React.ReactElement {
        const { loading, search } = this.state;
        const matches = this.filteredMatches();

        return (
            <MobileShell title="Livescore — pick a match">
                <Input.Search
                    placeholder="Search team or competition"
                    allowClear
                    value={search}
                    onChange={(e): void => this.setState({ search: e.target.value })}
                    style={{ marginBottom: 12 }}
                />

                <Spin spinning={loading}>
                    {!loading && matches.length === 0 ? (
                        <Empty description="No matches" />
                    ) : (
                        matches.map((match) => (
                            <div
                                key={match.id}
                                className="m-list__item"
                                role="button"
                                tabIndex={0}
                                onClick={(): void => this.openMatch(match.id)}
                                onKeyDown={(e): void => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        this.openMatch(match.id);
                                    }
                                }}
                            >
                                <div className="m-list__teams">
                                    <span>{match.homeTeamDisplayName}</span>
                                    <span className="m-list__score">{this.renderScore(match)}</span>
                                    <span>{match.awayTeamDisplayName}</span>
                                </div>
                                <div className="m-list__meta">
                                    {match.startDate
                                        ? moment(match.startDate).format('DD.MM.YYYY. HH:mm')
                                        : ''}
                                    {match.competitionDisplayName
                                        ? ` · ${match.competitionDisplayName}`
                                        : ''}
                                    {match.status ? ` · ${match.status.replace('_', ' ')}` : ''}
                                </div>
                            </div>
                        ))
                    )}
                </Spin>
            </MobileShell>
        );
    }
}

export default MobileMatchList;
